import React from 'react';
import { Box, Typography, Icons, IconButton } from '@ublend-npm/aulaui-core';
import { Dialog, Button } from '@mui/material';
import { useTheme } from '@ublend-npm/aulaui-core';

export interface RecentUpdatesInfoPopupProps {
  open: boolean;
  onClose: () => void;
}

const RecentUpdatesInfoPopup: React.FC<RecentUpdatesInfoPopupProps> = ({
  open,
  onClose,
}) => {
  const theme = useTheme();

  const isDark = theme.palette.mode === 'dark';
  const dialogBg = isDark ? '#352f3d' : '#ffffff';
  const textColor = isDark ? '#e2dcf0' : theme.palette.text.primary;
  const titleColor = isDark ? '#c4b2ff' : theme.palette.primary.main;
  const closeIconColor = isDark ? '#a79eb5' : theme.palette.text.secondary;
  const headingColor = isDark ? '#ffffff' : theme.palette.text.primary;
  const descriptionColor = isDark ? '#b0a8bd' : theme.palette.text.secondary;

  const educatorBulletColor = isDark ? '#ffffff' : theme.palette.primary.main;
  const studentBulletColor = isDark ? '#8a8594' : theme.palette.text.secondary;

  const btnBg = isDark ? '#d1c4e9' : theme.palette.primary.light;
  const btnTextColor = isDark ? '#2c1c5a' : theme.palette.primary.dark;
  const btnHoverBg = isDark ? '#bfaeff' : theme.palette.primary.main;
  const btnHoverTextColor = isDark ? '#2c1c5a' : '#ffffff';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          bgcolor: dialogBg,
          color: textColor,
          borderRadius: 3,
          padding: 3,
          maxWidth: 550,
          width: '100%',
          boxShadow: '0 8px 32px rgba(0,0,0,0.24)',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2.5,
        }}
      >
        <Typography variant="h6" fontWeight="bold" sx={{ color: titleColor }}>
          Recent updates
        </Typography>
        <IconButton
          onClick={onClose}
          aria-label="Close information dialog"
          size="small"
          sx={{
            color: closeIconColor,
            '&:hover': {
              color: theme.palette.text.primary,
            },
          }}
        >
          <Icons.Close fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                bgcolor: educatorBulletColor,
                flexShrink: 0,
              }}
            />
            <Typography
              variant="body1"
              fontWeight="bold"
              sx={{ color: headingColor }}
            >
              Educators post
            </Typography>
          </Box>
          <Typography
            variant="body2"
            sx={{
              color: descriptionColor,
              lineHeight: 1.6,
              pl: 3,
            }}
          >
            The color coding highlights posts shared by educators across the
            Common Room and Announcements. This helps users quickly identify
            official updates and important communication at a glance.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                bgcolor: studentBulletColor,
                flexShrink: 0,
              }}
            />
            <Typography
              variant="body1"
              fontWeight="bold"
              sx={{ color: headingColor }}
            >
              Student post
            </Typography>
          </Box>
          <Typography
            variant="body2"
            sx={{
              color: descriptionColor,
              lineHeight: 1.6,
              pl: 3,
            }}
          >
            The color coding highlights posts shared by learners in Common Room
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          onClick={onClose}
          variant="contained"
          disableElevation
          sx={{
            bgcolor: btnBg,
            color: btnTextColor,
            textTransform: 'none',
            borderRadius: 1.5,
            px: 3,
            py: 0.75,
            fontWeight: 'bold',
            '&:hover': {
              bgcolor: btnHoverBg,
              color: btnHoverTextColor,
            },
          }}
        >
          Close
        </Button>
      </Box>
    </Dialog>
  );
};

export default RecentUpdatesInfoPopup;
